/**
 * Slice LV-01 · useLeviVoiceIntake
 * ================================
 *
 * Owns the Levi voice-intake session lifecycle for the contractor:
 *   - creates a session on open
 *   - captures the contractor's voice via MediaRecorder (audio-only)
 *   - uploads each turn (multipart) to the backend for Whisper +
 *     GPT-5.2 extraction
 *   - exposes structured intake, pending question, next action,
 *     and finalize state to the UI
 *   - protects against duplicate finalize / duplicate mic events by
 *     tracking a client-side `finalizing` flag and letting the server
 *     enforce idempotency
 *
 * The primary contract is voice-first. `submitText()` is an optional
 * fallback exposed for accessibility / noisy environments / microphone
 * denial.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const CHUNK_MS = 800;

const pickAudioMime = () => {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const m of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* noop */
    }
  }
  return "";
};

export function useLeviVoiceIntake({ enabled, initialContext }) {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState("idle");
  // idle | booting | ready | requesting_mic | listening | uploading |
  // thinking | awaiting_confirmation | creating | finished | error
  const [error, setError] = useState("");
  const [micDenied, setMicDenied] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // ------------------------------------------------------------------
  // Session bootstrap — creates a fresh intake session the moment the
  // drawer opens, so Levi's greeting is visible immediately.
  //
  // LEVI-DOC-PREP-01 · Defect B · When the parent forwards a bounded
  // handoff context (project_id / customer_name / document_type) via
  // `initialContext`, we pass it to the POST body so the backend
  // pre-populates the greeting + structured intake. The two backend
  // session stores stay separate; only that bounded context crosses
  // the boundary.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      setStatus("booting");
      try {
        const body = {};
        if (initialContext && typeof initialContext === "object") {
          const pid = (initialContext.project_id || "").toString().trim();
          const cust = (initialContext.customer_name || "").toString().trim();
          const doc = (initialContext.document_type || "").toString().trim();
          if (pid) body.project_id = pid;
          if (cust) body.customer_name = cust;
          if (doc) body.document_type = doc;
        }
        const r = await api.post("/levi/intake/sessions", body);
        if (!cancelled) {
          setSession(r.data);
          setStatus("ready");
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || "Could not start Levi.");
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
      try {
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
      } catch {
        /* noop */
      }
    };
    // We intentionally read initialContext lazily on mount and do not
    // depend on it in the effect deps — a mid-session context change
    // must not spawn a second intake session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const bindStream = useCallback(async () => {
    if (streamRef.current) return streamRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setMicDenied(false);
      return stream;
    } catch (e) {
      setMicDenied(true);
      throw e;
    }
  }, []);

  const startListening = useCallback(async () => {
    if (!session) return;
    setError("");
    setStatus("requesting_mic");
    let stream;
    try {
      stream = await bindStream();
    } catch (e) {
      setError("Microphone permission denied. You can type your response instead.");
      setStatus("ready");
      return;
    }
    const mime = pickAudioMime();
    let mr;
    try {
      mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (e) {
      setError("This browser does not support voice recording. Please type instead.");
      setStatus("ready");
      return;
    }
    chunksRef.current = [];
    mr.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    mr.start(CHUNK_MS);
    recorderRef.current = mr;
    setStatus("listening");
  }, [bindStream, session]);

  const stopAndSend = useCallback(async () => {
    const mr = recorderRef.current;
    if (!mr) return;
    if (mr.state !== "inactive") {
      await new Promise((resolve) => {
        mr.addEventListener("stop", resolve, { once: true });
        try {
          mr.stop();
        } catch {
          resolve();
        }
      });
    }
    recorderRef.current = null;
    const mime = mr.mimeType || "audio/webm";
    const chunks = chunksRef.current;
    chunksRef.current = [];
    if (chunks.length === 0) {
      setStatus("ready");
      setError("No speech detected. Please try again.");
      return;
    }
    setStatus("uploading");
    const blob = new Blob(chunks, { type: mime });
    const ext = mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm";
    const fd = new FormData();
    fd.append("file", new File([blob], `levi-turn-${Date.now()}.${ext}`, { type: mime }));
    setStatus("thinking");
    try {
      const r = await api.post(
        `/levi/intake/sessions/${session.id}/turn`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSession(r.data);
      setStatus(r.data.status === "awaiting_confirmation" ? "awaiting_confirmation" : "ready");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Speech recognition failed. Please try again.");
      setStatus("ready");
    }
  }, [session]);

  const cancelListening = useCallback(() => {
    const mr = recorderRef.current;
    if (mr) {
      try {
        mr.stop();
      } catch {
        /* noop */
      }
      recorderRef.current = null;
    }
    chunksRef.current = [];
    setStatus("ready");
  }, []);

  const submitText = useCallback(async (text) => {
    if (!session || !text || !text.trim()) return;
    setStatus("thinking");
    setError("");
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      const r = await api.post(
        `/levi/intake/sessions/${session.id}/turn`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSession(r.data);
      setStatus(r.data.status === "awaiting_confirmation" ? "awaiting_confirmation" : "ready");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not send your response.");
      setStatus("ready");
    }
  }, [session]);

  const finalize = useCallback(async () => {
    if (!session) return null;
    if (finalizing) return null;              // client-side dedupe guard
    setFinalizing(true);
    setStatus("creating");
    setError("");
    try {
      const r = await api.post(`/levi/intake/sessions/${session.id}/finalize`);
      setSession(r.data);
      setStatus("finished");
      return r.data;
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not create the records.");
      setStatus("awaiting_confirmation");
      setFinalizing(false);
      return null;
    }
  }, [finalizing, session]);

  return useMemo(
    () => ({
      session, status, error, micDenied, finalizing,
      startListening, stopAndSend, cancelListening, submitText, finalize,
    }),
    [
      session, status, error, micDenied, finalizing,
      startListening, stopAndSend, cancelListening, submitText, finalize,
    ]
  );
}
